import React from 'react';

import {cn} from "@isudio/utils";

import {Label} from "../";

interface LoadingSpinnerProps {
  className?: string;
  borderRadius?: string;
  label?: string | null;
  image?: string;
  video?: string;
  noTransparency?: boolean;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
                                                         className = "loading-spinner",
                                                         borderRadius = "rounded-none",
                                                         label,
                                                         image,
                                                         video,
                                                         noTransparency = false,
                                                       }) => {
  const renderSource = video || image;
  const isVideoAsset = typeof renderSource === 'string' && /\.(mp4|webm|ogv|mov)(\?.*)?$/i.test(renderSource);

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center",
        noTransparency ? "bg-bluegray-darker" : "backdrop-blur-[1px] bg-bluegray-darker/85",
        `${borderRadius}`,
        `${className}`
      )}
      style={isVideoAsset ? {
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        willChange: 'transform',
      } : {}}
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
    >
      <div tabIndex={0} />

      <div className={cn(`relative w-auto h-auto flex flex-col items-center text-center`, `${!image && !video ? "animate-pulsate" : ``}`)}>
        <div className="relative w-[3em] h-[3em] rounded-full bg-transparent shadow-ionicGlow flex items-center justify-center z-10">
          <img
            src="/assets/icons/icon-128x128.png"
            alt="Logo"
            className="w-[1.5em] h-[1.5em] animate-rotateColors z-20"
          />

          <div className="absolute inset-[0.05em] z-10 animate-spin duration-1500">
            <svg viewBox="0 0 64 64" className="w-full h-full">
              <circle
                transform="translate(32,32)"
                r="26"
                fill="transparent"
                stroke="white"
                strokeWidth="4"
                strokeDasharray="128px"
                strokeDashoffset="82px"
              />
            </svg>
          </div>
        </div>

        <div>
          {label && (
            <Label className="loading mt-4 text-[10px] font-semibold text-bluegray-lighter">{label}</Label>
          )}
        </div>

        {renderSource && (
          <div className="w-full h-full mt-5 flex items-center justify-center">
            {isVideoAsset ? (
              /* --- Hardware Accelerated Video Loop Pipeline --- */
              <video
                src={renderSource}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                width={320}
                height={126}
                style={{ contain: 'strict' }}
                className="block object-contain pointer-events-none transform-gpu will-change-transform"
              />
            ) : (
              /* --- Traditional Image Element Fallback Track --- */
              <img
                src={renderSource}
                alt="Loading Animation"
                className="w-80 h-auto block object-contain"
              />
            )}
          </div>
        )}
      </div>

      <div tabIndex={0} />
    </div>
  );
};
